import { createAsyncThunk } from '@reduxjs/toolkit';
import { isOrderComplete } from '@src/utils';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { NEW_ORDER_SINGLE } from '../../../../../tokens';
import {
  OrdTypeEnum,
  SecurityTypesUtils,
  SideEnum,
  TimeInForceEnum,
  type CustomerOrder,
  type OrderFormSides,
} from '../../../../../types';
import type { WhiteLabelNewOrderSingle } from '../../../OrderService';
import type { WLAppExtraArgument, WLRootState } from '../../../WLOrderFormStore';
import { setSwapFocusedOrder, setSwapSentOrderPayload, setSwapStep } from '../reducer';
import { selectSecurityAndSide } from '../selector';

export const sendLimitOrderThunk = createAsyncThunk<
  CustomerOrder | undefined,
  void,
  {
    state: WLRootState;
    extra: WLAppExtraArgument | undefined;
  }
>('swap/sendLimitOrderThunk', async (_arg, { extra, dispatch, rejectWithValue, getState }) => {
  const wsClient = extra?.wsClient;
  if (!wsClient) {
    return;
  }
  const state = getState();
  const quote = state.swap.quote;
  const securityAndSide = selectSecurityAndSide(state);
  if (!securityAndSide || !quote || !SecurityTypesUtils.Spot.isType(securityAndSide.security)) {
    return undefined;
  }

  const formState = state.swap.form;
  const { security, side } = securityAndSide;

  // extract the qty that is equal to the baseCurrency of the security,
  const baseCurrencyQty =
    formState.fromCurrency.value?.Symbol === security.BaseCurrency ? formState.fromQty : formState.toQty;
  const price = quote[side === SideEnum.Buy ? 'OfferPx' : 'BidPx'];

  // prepare a limit base currency order
  const ClOrdID = uuid();
  const limitOrder: WhiteLabelNewOrderSingle = {
    Symbol: security.Symbol,
    ClOrdID: ClOrdID,
    Side: side as unknown as OrderFormSides,
    OrderQty: baseCurrencyQty.value ?? '',
    Price: price,
    Currency: security.BaseCurrency,
    Strategy: 'Limit',
    MarketAccount: formState.marketAccount.value?.SourceAccountID,
    TransactTime: new Date().toISOString(),
    TimeInForce: TimeInForceEnum.FillOrKill,
    OrdType: OrdTypeEnum.Limit,
    Parameters: {},
  };

  // Send the order
  wsClient.registerPublication({
    type: NEW_ORDER_SINGLE,
    data: [limitOrder],
  });
  dispatch(setSwapSentOrderPayload(limitOrder));

  // We need this order to be terminal, determined by the status
  const receivedOrder = await firstValueFrom(
    extra.sessionOrdersObs.pipe(
      filter(order => order.ClOrdID === ClOrdID),
      filter(order => isOrderComplete(order.OrdStatus)),
      timeout(5000)
    )
  ).catch(err => {
    return undefined;
  });
  if (!receivedOrder) {
    return rejectWithValue(
      'Did not receive an order update, please ensure a good internet connection and check your transactions'
    );
  }
  dispatch(setSwapFocusedOrder(receivedOrder));
  dispatch(setSwapStep('sentEntity'));
  return receivedOrder;
});
